import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { propertyAPI } from '../services/api';
import { ArrowLeft, Upload, X, Plus, Sparkles, Loader2 } from 'lucide-react';

const PropertyCreationForm = ({ onCancel, onSuccess }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [images, setImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  // AI listing import: the host pastes the description they already wrote for
  // Airbnb and we prefill this form from it. Suggestions only — they review
  // every field, and nothing is saved until they submit.
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    propertyType: 'Apartment',
    location: {
      address: '',
      city: '',
      postcode: '',
      country: 'United Kingdom'
    },
    capacity: {
      guests: 2,
      bedrooms: 1,
      beds: 1,
      bathrooms: 1
    },
    pricing: {
      nightlyRate: 100,
      cleaningFee: 50,
      serviceFee: 20,
      weeklyDiscount: 0,
      monthlyDiscount: 0,
      securityDeposit: 0
    },
    amenities: [],
    houseRules: '',
    checkInTime: '15:00',
    checkOutTime: '11:00',
    instantBook: false,
    minimumStay: 1,
    maximumStay: null,
    // Default to "moderate" to match Airbnb's default for new hosts.
    cancellationPolicy: 'moderate',
    // Advisory short-let insurance (capture only; hosts carry their own cover).
    insurance: {
      hasInsurance: false,
      insurer: '',
      policyNumber: '',
      expiry: ''
    },
    // STR compliance (advisory capture): council licensing + leasehold consent.
    councilLicenceNumber: '',
    councilBorough: '',
    isLeasehold: false,
    leaseholdConsentDeclared: false
  });

  const amenitiesList = [
    'Wifi', 'Kitchen', 'Washing machine', 'Dryer', 'Air conditioning', 
    'Heating', 'TV', 'Parking', 'Elevator', 'Pool', 'Gym', 'Hot tub',
    'Fireplace', 'Garden', 'Balcony', 'BBQ', 'Workspace', 'Pet friendly'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleImportDescription = async () => {
    if (!importText.trim() || importing) return;
    setImporting(true);
    try {
      const token = getAuthToken();
      const { fields } = await propertyAPI.parseDescription(importText, token);

      // A null means the description never said, so keep whatever the host
      // already has rather than blanking a field they may have filled in.
      setFormData(prev => ({
        ...prev,
        title: fields.title ?? prev.title,
        description: fields.description ?? prev.description,
        propertyType: fields.propertyType ?? prev.propertyType,
        houseRules: fields.houseRules ?? prev.houseRules,
        checkInTime: fields.checkInTime ?? prev.checkInTime,
        checkOutTime: fields.checkOutTime ?? prev.checkOutTime,
        amenities: fields.amenities?.length ? fields.amenities : prev.amenities,
        capacity: {
          ...prev.capacity,
          guests: fields.maxGuests ?? prev.capacity.guests,
          bedrooms: fields.bedrooms ?? prev.capacity.bedrooms,
          bathrooms: fields.bathrooms ?? prev.capacity.bathrooms,
        },
      }));

      setImportOpen(false);
      toast({
        title: 'Listing imported',
        description: "Check it over — we only filled in what your description actually said.",
      });
    } catch (error) {
      toast({
        title: error.response?.status === 503 ? 'Importer unavailable' : 'Could not import',
        description:
          error.response?.data?.detail ||
          'Something went wrong. You can still fill the form in by hand.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);
    
    // Create preview URLs
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (status = 'draft') => {
    setLoading(true);
    try {
      // Create property
      const token = getAuthToken();
      const property = await propertyAPI.createProperty({
        ...formData,
        images: [] // Will be added after upload
      }, token);

      // Upload images if any
      if (images.length > 0) {
        await propertyAPI.uploadImages(property._id, images, token);
      }

      // Update status if publishing
      if (status === 'published') {
        await propertyAPI.updatePropertyStatus(property._id, 'published', token);
      }

      toast({
        title: status === 'published' ? 'Property Published!' : 'Property Saved as Draft',
        description: status === 'published' 
          ? 'Your property is now live and visible to guests.' 
          : 'You can publish it later from your dashboard.'
      });

      if (onSuccess) {
        onSuccess(property);
      } else {
        navigate('/host/dashboard');
      }
    } catch (error) {
      console.error('Error creating property:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create property. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onCancel || (() => navigate('/host/dashboard'))}>
            <ArrowLeft size={20} />
          </Button>
          <h2 className="text-3xl font-bold text-[#1F3A68]">Create New Property</h2>
        </div>
        <div className="text-sm text-gray-600">Step {currentStep} of 4</div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {['Basic Info', 'Details', 'Pricing', 'Images'].map((step, index) => (
            <div
              key={index}
              className={`text-sm font-semibold ${
                currentStep > index ? 'text-[#1F3A68]' : 'text-gray-400'
              }`}
            >
              {step}
            </div>
          ))}
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-full bg-[#1F3A68] rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Step 1: Basic Info */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Import from a listing the host already wrote elsewhere. */}
            {!importOpen ? (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#1F3A68]/30 rounded-lg text-[#1F3A68] hover:bg-[#1F3A68]/5 transition-colors"
                data-testid="open-import-btn"
              >
                <Sparkles size={18} className="shrink-0" />
                <span className="text-sm font-semibold text-left">
                  Already listed elsewhere? Paste your description and we'll fill this in
                </span>
              </button>
            ) : (
              <div className="p-4 border-2 border-[#1F3A68]/30 rounded-lg bg-[#1F3A68]/5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#1F3A68] flex items-center gap-2">
                      <Sparkles size={16} /> Import your listing
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Paste the description from your Airbnb listing, or anywhere else you've written it.
                      We'll fill in what it tells us and leave the rest to you.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setImportOpen(false)}
                    className="text-gray-400 hover:text-gray-600 shrink-0"
                    aria-label="Close importer"
                  >
                    <X size={18} />
                  </button>
                </div>
                <Textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="Paste your existing property description here..."
                  rows={6}
                  data-testid="import-textarea"
                />
                <Button
                  type="button"
                  onClick={handleImportDescription}
                  disabled={!importText.trim() || importing}
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  data-testid="import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Reading your description...
                    </>
                  ) : (
                    'Fill in my listing'
                  )}
                </Button>
              </div>
            )}

            <div>
              <Label>Property Title *</Label>
              <Input
                placeholder="e.g., Modern Apartment in City Centre"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe your property..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={6}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Property Type *</Label>
              <select
                value={formData.propertyType}
                onChange={(e) => handleInputChange('propertyType', e.target.value)}
                className="w-full mt-1 h-10 px-3 border border-gray-300 rounded-lg"
              >
                <option value="Apartment">Apartment</option>
                <option value="House">House</option>
                <option value="Loft">Loft</option>
                <option value="Studio">Studio</option>
                <option value="Villa">Villa</option>
                <option value="Cottage">Cottage</option>
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Address *</Label>
                <Input
                  value={formData.location.address}
                  onChange={(e) => handleNestedChange('location', 'address', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>City *</Label>
                <Input
                  value={formData.location.city}
                  onChange={(e) => handleNestedChange('location', 'city', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Postcode *</Label>
                <Input
                  value={formData.location.postcode}
                  onChange={(e) => handleNestedChange('location', 'postcode', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.location.country}
                  onChange={(e) => handleNestedChange('location', 'country', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Details */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Property Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="mb-3 block">Capacity</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm">Guests</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.capacity.guests}
                    onChange={(e) => handleNestedChange('capacity', 'guests', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Bedrooms</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.capacity.bedrooms}
                    onChange={(e) => handleNestedChange('capacity', 'bedrooms', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Beds</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.capacity.beds}
                    onChange={(e) => handleNestedChange('capacity', 'beds', parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Bathrooms</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.capacity.bathrooms}
                    onChange={(e) => handleNestedChange('capacity', 'bathrooms', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Amenities</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {amenitiesList.map(amenity => (
                  <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => handleAmenityToggle(amenity)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">{amenity}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>House Rules</Label>
              <Textarea
                placeholder="e.g., No smoking, No parties, etc."
                value={formData.houseRules}
                onChange={(e) => handleInputChange('houseRules', e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Check-in Time</Label>
                <Input
                  type="time"
                  value={formData.checkInTime}
                  onChange={(e) => handleInputChange('checkInTime', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Check-out Time</Label>
                <Input
                  type="time"
                  value={formData.checkOutTime}
                  onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Minimum Stay (nights)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.minimumStay}
                  onChange={(e) => handleInputChange('minimumStay', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.instantBook}
                  onChange={(e) => handleInputChange('instantBook', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="font-semibold">Enable Instant Booking</span>
              </label>
              <p className="text-sm text-gray-600 mt-1 ml-6">
                Guests can book without waiting for your approval
              </p>
            </div>

            {/* Short-let insurance (advisory, optional) */}
            <div className="border-t pt-5 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.insurance.hasInsurance}
                  onChange={(e) => handleNestedChange('insurance', 'hasInsurance', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="font-semibold">I have short let insurance for this property</span>
              </label>
              <p className="text-sm text-gray-600 mt-1 ml-6">
                Ariv does not provide insurance. You are responsible for holding cover suitable for short lets. Adding the details here is optional and just helps you keep track.
              </p>
              {formData.insurance.hasInsurance && (
                <div className="grid sm:grid-cols-3 gap-3 mt-3 ml-6">
                  <div>
                    <Label>Insurer</Label>
                    <Input
                      value={formData.insurance.insurer}
                      onChange={(e) => handleNestedChange('insurance', 'insurer', e.target.value)}
                      placeholder="e.g. Allianz"
                    />
                  </div>
                  <div>
                    <Label>Policy number</Label>
                    <Input
                      value={formData.insurance.policyNumber}
                      onChange={(e) => handleNestedChange('insurance', 'policyNumber', e.target.value)}
                      placeholder="Policy number"
                    />
                  </div>
                  <div>
                    <Label>Expiry date</Label>
                    <Input
                      type="date"
                      value={formData.insurance.expiry}
                      onChange={(e) => handleNestedChange('insurance', 'expiry', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Council licensing + leasehold (advisory, optional) */}
            <div className="border-t pt-5 mt-2">
              <p className="font-semibold">Council licensing</p>
              <p className="text-sm text-gray-600 mt-1">
                Some councils require short-let registration or a licence. If yours does, record the
                details here.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label>Licence / registration number</Label>
                  <Input
                    value={formData.councilLicenceNumber}
                    onChange={(e) => handleInputChange('councilLicenceNumber', e.target.value)}
                    placeholder="e.g. STL-2026-01234"
                  />
                </div>
                <div>
                  <Label>Council / borough</Label>
                  <Input
                    value={formData.councilBorough}
                    onChange={(e) => handleInputChange('councilBorough', e.target.value)}
                    placeholder="e.g. London Borough of Camden"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-5 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isLeasehold}
                  onChange={(e) => handleInputChange('isLeasehold', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="font-semibold">This property is leasehold</span>
              </label>
              {formData.isLeasehold && (
                <label className="flex items-start gap-2 cursor-pointer mt-3 ml-6">
                  <input
                    type="checkbox"
                    checked={formData.leaseholdConsentDeclared}
                    onChange={(e) => handleInputChange('leaseholdConsentDeclared', e.target.checked)}
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <span className="text-sm text-gray-700">
                    I declare my lease (or my freeholder) permits short-term letting. Many leases
                    restrict short lets — you are responsible for checking yours before hosting.
                  </span>
                </label>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Pricing */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nightly Rate (£) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.pricing.nightlyRate}
                  onChange={(e) => handleNestedChange('pricing', 'nightlyRate', parseFloat(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Cleaning Fee (£)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.pricing.cleaningFee}
                  onChange={(e) => handleNestedChange('pricing', 'cleaningFee', parseFloat(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Service Fee (£)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.pricing.serviceFee}
                  onChange={(e) => handleNestedChange('pricing', 'serviceFee', parseFloat(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Security Deposit (£)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.pricing.securityDeposit}
                  onChange={(e) => handleNestedChange('pricing', 'securityDeposit', parseFloat(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Discounts (%)</Label>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Weekly Discount</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.pricing.weeklyDiscount}
                    onChange={(e) => handleNestedChange('pricing', 'weeklyDiscount', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Monthly Discount</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.pricing.monthlyDiscount}
                    onChange={(e) => handleNestedChange('pricing', 'monthlyDiscount', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Cancellation Policy — Airbnb-style five-tier selector. The
                snapshot of the chosen policy gets attached to every booking
                at the moment the guest pays, so future policy changes don't
                retroactively affect existing reservations. */}
            <div className="pt-4 border-t">
              <Label className="text-base font-semibold">Cancellation Policy</Label>
              <p className="text-sm text-gray-600 mt-1 mb-4">
                Decides how much guests get back if they cancel after paying.
                You can change this later — existing bookings keep the policy
                that was in effect when they were made.
              </p>
              <div className="space-y-2">
                {[
                  { id: 'flexible', label: 'Flexible', headline: 'Full refund up to 24 hours before check-in.' },
                  { id: 'moderate', label: 'Moderate', headline: 'Full refund up to 5 days before check-in.' },
                  { id: 'firm', label: 'Firm', headline: 'Full refund up to 30 days, 50% inside 7-30 days.' },
                  { id: 'strict', label: 'Strict', headline: '50% refund up to 7 days before, none after.' },
                  { id: 'long_term', label: 'Long-term (28+ nights)', headline: 'First month non-refundable, then 30-day notice.' },
                ].map(opt => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.cancellationPolicy === opt.id
                        ? 'border-[#1F3A68] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancellationPolicy"
                      value={opt.id}
                      checked={formData.cancellationPolicy === opt.id}
                      onChange={() => handleInputChange('cancellationPolicy', opt.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-[#1F3A68]">{opt.label}</div>
                      <div className="text-sm text-gray-600">{opt.headline}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Images */}
      {currentStep === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Property Images</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#1F3A68] transition-colors"
              onClick={() => document.getElementById('image-upload').click()}
            >
              <Upload className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="mb-2 text-gray-700">Drag & drop images here or click to browse</p>
              <p className="text-sm text-gray-500 mb-4">Upload up to 10 images (JPG, PNG)</p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <Button 
                type="button" 
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('image-upload').click();
                }}
              >
                <Plus size={16} className="mr-2" />
                Select Images
              </Button>
            </div>

            {imagePreview.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                {imagePreview.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
          disabled={currentStep === 1}
        >
          Previous
        </Button>

        <div className="flex gap-3">
          {currentStep === 4 ? (
            <>
              <Button
                variant="outline"
                onClick={() => handleSubmit('draft')}
                disabled={loading}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit('published')}
                disabled={loading}
                className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              >
                {loading ? 'Publishing...' : 'Publish Property'}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setCurrentStep(prev => Math.min(4, prev + 1))}
              className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCreationForm;
